import { useCallback, useEffect, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

export function StockDialog({ dep, onClose }) {
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_id: "", qty: "", reference: "", override: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/stock/${dep.agent_id}`).then((r) => setData(r.data)), [dep.agent_id]);
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data.filter((p) => p.active !== false)));
  }, [load]);

  const move = async (kind) => {
    if (!form.product_id || !(Number(form.qty) > 0)) return toast.error("Choose a product and quantity");
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/stock/${kind}`, {
        agent_id: dep.agent_id, product_id: form.product_id, qty: Number(form.qty),
        reference: form.reference, override: form.override,
      });
      toast.success(`${kind === "issue" ? "Issued" : "Returned"} ${form.qty} bag(s) · credit used now ${money(r.outstanding_credit)} / ${money(r.credit_limit)}`);
      setForm({ ...form, qty: "", reference: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Stock on credit · {dep.agent_name}</DialogTitle></DialogHeader>
        {!data ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : (
          <>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Stat l="Unsold bags value" v={money(data.unsold_value)} />
              <Stat l={`Unpaid invoices (${data.unpaid_invoices})`} v={money(data.unpaid_invoices_value)} />
              <Stat l="Credit used / limit" v={`${money(data.outstanding)} / ${money(dep.credit_limit)}`} warn={data.outstanding > dep.credit_limit} />
            </div>

            <div className="rounded-lg border border-border p-3" data-testid="stock-held">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Bags currently held (unsold)</p>
              {data.stock_held.length === 0 ? <p className="text-xs text-muted-foreground">No unsold stock on credit.</p> : data.stock_held.map((h) => (
                <div key={h.product_id} className="flex justify-between text-sm py-1 border-b border-border/40 last:border-0"><span>{h.qty} × {h.product}</span><span>{money(h.value)}</span></div>
              ))}
            </div>

            <div className="rounded-lg bg-secondary/40 p-3 space-y-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Issue / return stock</p>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-6">
                  <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                    <SelectTrigger className="h-9" data-testid="stock-product-select"><SelectValue placeholder="Product" /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.pack} · {p.list_price} AED</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input className="col-span-2 h-9" type="number" min="1" placeholder="Qty" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="stock-qty-input" />
                <Input className="col-span-4 h-9" placeholder="Delivery note / ref" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="stock-ref-input" />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Checkbox checked={form.override} onCheckedChange={(v) => setForm({ ...form, override: !!v })} data-testid="stock-override-checkbox" /> Override credit-limit / top-up block
                </label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => move("return")} data-testid="stock-return-button"><ArrowDownToLine className="w-3.5 h-3.5 mr-1" /> Return</Button>
                  <Button size="sm" disabled={busy} onClick={() => move("issue")} data-testid="stock-issue-button"><ArrowUpFromLine className="w-3.5 h-3.5 mr-1" /> Issue</Button>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Stock ledger</p>
              {data.ledger.length === 0 ? <p className="text-xs text-muted-foreground">No movements yet.</p> : (
                <table className="w-full text-xs">
                  <tbody>{data.ledger.map((e) => (
                    <tr key={e.id} className="border-b border-border/40" data-testid={`stock-ledger-${e.id}`}>
                      <td className="py-1.5 text-muted-foreground">{e.date}</td>
                      <td className="py-1.5"><Badge variant={e.type === "issued" ? "default" : "secondary"}>{e.type}</Badge></td>
                      <td className="py-1.5">{e.qty} × {e.product}</td>
                      <td className="py-1.5 text-muted-foreground">{e.reference}{e.override && " · override"}</td>
                      <td className="py-1.5 text-right">{money(e.value)}</td>
                    </tr>))}</tbody>
                </table>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ l, v, warn }) {
  return <div className="rounded-lg border border-border p-3"><p className="text-[11px] text-muted-foreground">{l}</p><p className={`font-semibold ${warn ? "text-destructive" : ""}`}>{v}</p></div>;
}
